
import React from 'react';
import { AppConfig, User, Conversation, IntegrationFlow, AgentProfile, ServiceDefinition, Attachment, AppMode, Plan, IntegrationApp } from '../../types';
import ChatTemplate from '../../templates/Chat/ChatTemplate';
import FlowsTemplate from '../../templates/Flows/FlowsTemplate';
import ServicesTemplate from '../../templates/Services/ServicesTemplate';
import RobotTemplate from '../../templates/Robot/RobotTemplate';
import ProfileTemplate from '../../templates/Profile/ProfileTemplate';
import IntegrationsTemplate from '../../templates/Integrations/IntegrationsTemplate';
import DocsTemplate from '../../templates/Docs/DocsTemplate';
import AdminTemplate from '../../templates/Admin/AdminTemplate';
import TerminalTemplate from '../../templates/Terminal/TerminalTemplate';

interface AppContentProps {
  config: AppConfig;
  user: User | null;
  usageCount: number;
  conversations: Conversation[];
  currentId: string | null;
  flows: IntegrationFlow[];
  agents: AgentProfile[];
  isLoading: boolean;
  isLiveActive: boolean;
  // New State props
  services: ServiceDefinition[];
  setServices: React.Dispatch<React.SetStateAction<ServiceDefinition[]>>;
  plans: Plan[];
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
  integrations: IntegrationApp[];
  setIntegrations: React.Dispatch<React.SetStateAction<IntegrationApp[]>>;
  
  setIsLiveActive: (active: boolean) => void;
  setAppMode: (mode: AppMode) => void;
  setAgents: React.Dispatch<React.SetStateAction<AgentProfile[]>>;
  setFlows: React.Dispatch<React.SetStateAction<IntegrationFlow[]>>;
  onUpdateUser: (u: Partial<User>) => void;
  onOpenPlans: () => void;
  onNavOpen: () => void;
  onUpdateConfig: (config: Partial<AppConfig>) => void;
  
  onSendMessage: (content: string, attachments?: Attachment[]) => void;
  onAddFlow: (flow: Omit<IntegrationFlow, 'id'>) => void;
  onDeleteFlow: (id: string) => void;
  onToggleFlow: (id: string) => void;
  onSelectService: (serviceName: string) => void;
  onAutomateService: (service: ServiceDefinition) => void;
  onExecuteCommand: (action: string, params?: any) => void;
  onOpenSettingsAt: (tab: 'profile' | 'app' | 'security') => void;
}

const AppContent: React.FC<AppContentProps> = ({
  config, user, usageCount, conversations, currentId, flows, agents,
  isLoading, isLiveActive, services, setServices, plans, setPlans, integrations, setIntegrations,
  setIsLiveActive, setAppMode, setAgents, setFlows,
  onUpdateUser, onOpenPlans, onNavOpen, onUpdateConfig, onSendMessage, onAddFlow, onDeleteFlow,
  onToggleFlow, onSelectService, onAutomateService, onExecuteCommand, onOpenSettingsAt
}) => {
  const currentConversation = conversations.find(c => c.id === currentId);

  // Fix: User type uses 'role' instead of 'isAdmin'. Checking for administrative roles.
  const isAdmin = user?.role === 'admin' || user?.role === 'super' || user?.role === 'superadmin';

  if (config.mode === 'admin' && isAdmin) {
    return (
      <AdminTemplate 
        language={config.language}
        onBack={() => setAppMode('chat')}
        plans={plans}
        setPlans={setPlans}
        services={services}
        setServices={setServices}
        integrations={integrations}
        setIntegrations={setIntegrations}
        config={config}
        onUpdateConfig={onUpdateConfig}
      />
    );
  }

  if (config.mode === 'terminal') {
     return (
        <TerminalTemplate 
          language={config.language}
          onBack={() => setAppMode('chat')}
          onExecuteCommand={onExecuteCommand}
        />
     );
  }

  if (config.mode === 'profile' && user) {
    return (
      <ProfileTemplate 
        user={user}
        language={config.language}
        usageCount={usageCount}
        flows={flows} 
        agents={agents}
        onManageBilling={onOpenPlans}
        onBack={() => setAppMode('chat')}
        setAgents={setAgents}
        setFlows={setFlows}
        onUpdateUser={onUpdateUser}
      />
    );
  }

  if (config.mode === 'robot' && user) {
    return (
      <React.Fragment>
         <div className="absolute top-0 left-0 right-0 p-4 z-50 flex justify-between items-center pointer-events-none">
            <div></div>
            <button onClick={onNavOpen} className="pointer-events-auto w-10 h-10 rounded-xl bg-black/20 text-white flex items-center justify-center backdrop-blur-sm lg:hidden"><i className="fas fa-grip-vertical"></i></button>
         </div>
         <RobotTemplate 
           language={config.language} 
           dialect={config.dialect}
           voice={config.voice}
           speed={config.speechSpeed}
           user={user} 
           onBack={() => setAppMode('chat')}
           agents={agents}
           setAgents={setAgents}
         />
      </React.Fragment>
    );
  }

  if (config.mode === 'integrations') {
    return (
      <React.Fragment>
         <div className="absolute top-0 left-0 right-0 p-4 z-50 flex justify-between items-center pointer-events-none">
            <div></div>
            <button onClick={onNavOpen} className="pointer-events-auto w-10 h-10 rounded-xl bg-black/20 text-white flex items-center justify-center backdrop-blur-sm lg:hidden"><i className="fas fa-grip-vertical"></i></button>
         </div>
         <IntegrationsTemplate 
           language={config.language} 
           config={config}
           apps={integrations}
           setApps={setIntegrations}
         />
      </React.Fragment>
    );
  }

  if (config.mode === 'docs') {
    return (
      <React.Fragment>
         <div className="absolute top-0 left-0 right-0 p-4 z-50 flex justify-between items-center pointer-events-none lg:hidden">
            <div></div>
            <button onClick={onNavOpen} className="pointer-events-auto w-10 h-10 rounded-xl bg-black/20 text-white flex items-center justify-center backdrop-blur-sm"><i className="fas fa-grip-vertical"></i></button>
         </div>
         <DocsTemplate 
           language={config.language} 
           onBack={() => setAppMode('chat')}
         />
      </React.Fragment>
    );
  }

  if (config.mode === 'flows' && user) {
    return (
      <FlowsTemplate 
        language={config.language} 
        user={user}
        flows={flows} 
        onAddFlow={onAddFlow} 
        onDeleteFlow={onDeleteFlow} 
        onToggleFlow={onToggleFlow}
      />
    );
  }

  if (config.mode === 'services' && user) {
    return (
      ServicesTemplate 
        ? <ServicesTemplate 
          language={config.language}
          user={user}
          services={services}
          onSelectService={onSelectService}
          onAutomateService={onAutomateService}
          onOpenPlans={onOpenPlans}
        />
        : null
    );
  }

  return (
    <ChatTemplate 
      messages={currentConversation?.messages || []}
      isLoading={isLoading}
      onSendMessage={onSendMessage}
      dialect={config.dialect}
      mode={config.mode}
      isLiveActive={isLiveActive}
      setIsLiveActive={setIsLiveActive}
      voice={config.voice}
      speechSpeed={config.speechSpeed}
      language={config.language}
      apiKey={config.apiKey}
      onOpenSettingsAt={onOpenSettingsAt}
    />
  );
};
export default AppContent;
